// ENCYCLOPEDIA screen (title menu → ENCYCLOPEDIA). Tabs ENEMIES / SKILLS / TRAITS / BOONS (LB / RB or [ ]), a grid of
// entries (locked ones are "???" silhouettes) and a detail panel for the focused entry. Data and rules: src/app/codex.ts.
// Keyboard (arrows / WASD), mouse, touch and gamepad. ?codexall previews everything unlocked without touching the save.
import { MAX_WEAPON_LEVEL, PassiveId, TechType, WeaponId } from '../core/content';
import { Sim } from '../core/sim';
import { passiveStats, skillStats } from '../core/skillStats';
import { enemyPortrait } from '../render/portrait';
import {
  BoonDetail, FormDetail, ItemDetail, CODEX_TABS, Codex, CodexDetail, CodexTab, EnemyDetail, LockedDetail, SkillDetail, TAB_LABEL, TraitDetail,
  catalogue, counts, detail, enemyArtOf, formByKey, freshKey, tiles,
} from './codex';
import { statGrid } from './hud';
import { fusionIcon, techIcon, traitIcon } from './icons';
import { EVO_FLOOR, FUSIONS, type FusionId } from '../core/fusions';
import { fusionStatGrid } from './fusionUi';
import type { FusionDetail } from './codex';
import type { UiAction } from './input';

export interface CodexHost {
  codex(): Codex;
  /** persist the profile (only called outside ?codexall) */
  save(): void;
  click(): void;
  close(): void;
  /** [previous, next] tab button labels for the current device */
  tabLabels(): [string, string];
  /** show everything unlocked (?codexall): never writes to the save */
  all: boolean;
}

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;
const TYPE_LABEL: Record<TechType, string> = {
  barrage: 'BARRAGE', orbit: 'ORBIT', blast: 'BLAST', chain: 'CHAIN', beam: 'BEAM', disc: 'DISC', artillery: 'ARTILLERY', form: 'FORM',
  sphere: 'SPHERE', cannon: 'CANNON', sweep: 'SWEEP', rain: 'RAIN', lure: 'LURE', flash: 'FLASH', melee: 'MELEE', boost: 'BARRIER', needle: 'NEEDLE',
  bind: 'BIND', grip: 'GRIP', ricochet: 'RICOCHET', snare: 'SNARE', flame: 'FLAME',
  frost: 'FROST', // ICE FAMILY (owner 2026-11-03)
};
const TILE_PX = 128, BIG_PX = 320;
/** SURGE FORMS: a round flame swatch in the form's palette (core → inner → outer, accent rim); inline so the stylesheet is untouched */
function formSwatch(key: string, size: 'sm' | ''): string {
  const f = formByKey(key); if (!f) return '<span class="cx-q">?</span>';
  const hex = (v: number) => `#${v.toString(16).padStart(6, '0')}`, px = size === 'sm' ? 56 : 112;
  return `<span class="cx-form${size ? ' ' + size : ''}" data-tier="${f.tier}" style="display:inline-block;width:${px}px;height:${px}px;border-radius:50%;background:radial-gradient(circle at 50% 55%, ${hex(f.core)} 0%, ${hex(f.inner)} ${28 + f.intensity * 10}%, ${hex(f.outer)} 70%, transparent 100%);box-shadow:0 0 ${8 + f.intensity * 22}px ${hex(f.motif === 'abyss' ? f.accent : f.outer)}, inset 0 0 0 ${f.motif === 'abyss' ? Math.round(px / 14) : 2}px ${hex(f.accent)}${f.motif === 'abyss' ? '' : '66'}" aria-hidden="true"></span>`;
}

export class CodexMenu {
  tab = 0;
  private sel: Record<CodexTab, number> = { enemies: 0, skills: 0, forms: 0, traits: 0, boons: 0, fusions: 0, items: 0 };
  /** detail panel has focus: ↑↓ scroll it */
  reading = false;
  private base: Sim | null = null;
  /** QA (?codexcheck): show this codex / unlock state instead of the profile's; never saved */
  preview: { codex?: Codex; all?: boolean } | null = null;
  private job = 0;
  private dirty = false;

  constructor(private host: CodexHost) {
    $('#cx-tabs').addEventListener('click', (e) => {
      const el = e.target as HTMLElement, t = el.closest('.stab') as HTMLElement | null, b = el.closest('.bump') as HTMLElement | null;
      if (t) this.setTab(Number(t.dataset.t)); else if (b) this.setTab(this.tab + Number(b.dataset.d));
    });
    $('#cx-grid').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('.cx-tile') as HTMLElement | null; if (!b) return;
      this.select(Number(b.dataset.i), true);
    });
    $('#cx-detail').addEventListener('click', () => { if (!this.reading) { this.reading = true; this.paintFocus(); } });
    $('#cx-back').addEventListener('click', () => this.close());
  }

  get curTab(): CodexTab { return CODEX_TABS[this.tab]; }
  private get codex(): Codex { return this.preview?.codex ?? this.host.codex(); }
  private get all(): boolean { return this.preview?.all ?? this.host.all; }

  open(): void { this.reading = false; this.render(); }
  close(): void {
    this.reading = false;
    if (this.dirty && !this.all && !this.preview) this.host.save();
    this.dirty = false;
    this.host.close();
  }

  setTab(i: number): void {
    this.tab = (i + CODEX_TABS.length) % CODEX_TABS.length; this.reading = false;
    this.host.click(); this.render();
  }

  /** focus entry i of the current tab */
  select(i: number, fromPointer = false): void {
    const n = catalogue(this.curTab).length; if (!n) return;
    const j = Math.max(0, Math.min(n - 1, i));
    if (j !== this.sel[this.curTab]) this.host.click();
    this.sel[this.curTab] = j; this.reading = false;
    this.render(fromPointer);
  }

  /** grid columns as laid out right now (arrow up/down move a whole row) */
  private cols(): number { return Math.max(1, getComputedStyle($('#cx-grid')).gridTemplateColumns.split(' ').filter(Boolean).length); }

  handle(a: UiAction): void {
    const n = catalogue(this.curTab).length, i = this.sel[this.curTab];
    if (a === 'back' || a === 'pause') { if (this.reading) { this.reading = false; this.paintFocus(); } else this.close(); return; }
    if (a === 'tabPrev') return this.setTab(this.tab - 1);
    if (a === 'tabNext') return this.setTab(this.tab + 1);
    if (a === 'confirm') { this.reading = !this.reading; this.host.click(); this.paintFocus(); return; }
    if (this.reading && (a === 'up' || a === 'down')) {
      const d = $('#cx-detail'); d.scrollBy({ top: (a === 'down' ? 1 : -1) * Math.max(60, d.clientHeight * 0.4), behavior: 'smooth' }); return;
    }
    const c = this.cols();
    if (a === 'right') this.select((i + 1) % n);
    else if (a === 'left') this.select((i + n - 1) % n);
    else if (a === 'down') this.select(i + c < n ? i + c : i);
    else if (a === 'up') this.select(i - c >= 0 ? i - c : i);
  }

  private paintFocus(): void {
    $('#cx-detail').classList.toggle('reading', this.reading);
    $('#cx-grid').classList.toggle('dim', this.reading);
  }

  render(fromPointer = false): void {
    const tab = this.curTab, all = this.all, c = this.codex;
    const [bl, br] = this.host.tabLabels();
    $('#cx-tabs').innerHTML = `<b class="bump" data-d="-1">◀ ${bl}</b>`
      + CODEX_TABS.map((t, k) => { const q = counts(t, c, all); return `<span class="stab${k === this.tab ? ' on' : ''}" data-t="${k}">${TAB_LABEL[t]} <small>${q.found}/${q.total}</small></span>`; }).join('')
      + `<b class="bump" data-d="1">${br} ▶</b>`;
    const q = counts(tab, c, all);
    $('#cx-count').innerHTML = `<b>${q.found} / ${q.total}</b> discovered${all ? ' <em>(preview: everything unlocked)</em>' : ''}`;
    const ts = tiles(tab, c, all), sel = Math.min(this.sel[tab], ts.length - 1);
    $('#cx-grid').innerHTML = ts.map((t, i) => {
      const pic = this.tilePic(tab, t.key, t.locked, i);
      return `<button class="cx-tile${this.curTab === 'fusions' ? ' fu' : ''}${t.locked ? ' locked' : ''}${i === sel ? ' focus' : ''}${t.badge === '★' ? ' evo' : ''}${t.badge === 'BOSS' ? ' boss' : t.badge === 'ELITE' ? ' elite' : ''}" data-i="${i}" aria-label="${t.locked ? 'Locked entry' : t.name}">`
        + `<span class="cx-pic">${pic}</span><span class="cx-nm">${t.name}</span>`
        + (t.badge ? `<span class="cx-bd">${t.badge}</span>` : '') + (t.fresh ? '<span class="cx-new">NEW</span>' : '') + '</button>';
    }).join('');
    const key = ts[sel]?.key;
    if (key !== undefined) {
      const d = detail(tab, key, c, all);
      $('#cx-detail').innerHTML = 'locked' in d ? this.lockedHtml(tab, key, d) : this.detailHtml(d);
      $('#cx-detail').scrollTop = 0;
      // looking at a NEW entry clears its badge (not in the ?codexall preview, which never writes)
      const fk = freshKey(tab, key), fi = c.fresh.indexOf(fk);
      if (!all && !('locked' in d) && fi >= 0) { c.fresh.splice(fi, 1); this.dirty = true; }
    }
    this.paintFocus();
    const f = document.querySelector<HTMLElement>('#cx-grid .cx-tile.focus');
    if (f && !fromPointer) f.scrollIntoView({ block: 'nearest' });
    this.fillPortraits();
  }

  // ------------------------------------------------------------------------------------------------------------------
  private tilePic(tab: CodexTab, key: string, locked: boolean, i: number): string {
    if (tab === 'enemies') return `<img class="cx-por" data-pi="${i}" data-sil="${locked ? 1 : 0}" alt="" draggable="false">`;
    if (locked) return tab === 'fusions' ? '<span class="cx-fu"><span class="cx-q sm">?</span><i>+</i><span class="cx-q sm">?</span></span>' : '<span class="cx-q">?</span>';
    if (tab === 'skills') return techIcon(key as WeaponId, this.all || (this.codex.skills[key]?.evolved ?? 0) > 0);
    if (tab === 'traits') return traitIcon(key as PassiveId);
    if (tab === 'forms') return formSwatch(key, 'sm'); // SURGE FORMS: the form's flame colours
    if (tab === 'items') return `<span class="cx-glyph">${(detail('items', key, this.codex, true) as ItemDetail).icon}</span>`;
    if (tab === 'fusions') { // SKILL FUSIONS: '? + ?' silhouettes until revealed, then the two partners, then the fusion itself
      const f = FUSIONS[key as FusionId], made = this.all || (this.codex.fusions[key]?.made ?? 0) > 0;
      return made ? fusionIcon(f.id) : `<span class="cx-fu">${techIcon(f.a)}<i>+</i>${techIcon(f.b)}</span>`;
    }
    const d = detail('boons', key, this.codex, true) as BoonDetail;
    return `<span class="cx-glyph">${d.icon}</span>`;
  }

  /** draw enemy portraits a few at a time (each is a one-off software render, then cached) */
  private fillPortraits(): void {
    const job = ++this.job, tab = this.curTab; if (tab !== 'enemies') return;
    const keys = catalogue('enemies');
    const imgs = [...document.querySelectorAll<HTMLImageElement>('#cx-grid img.cx-por, #cx-detail img.cx-por')].filter((im) => !im.src);
    const step = () => {
      if (job !== this.job) return;
      const t0 = performance.now();
      while (imgs.length && performance.now() - t0 < 12) {
        const im = imgs.shift()!;
        const key = im.dataset.pk ?? keys[Number(im.dataset.pi)];
        const url = enemyPortrait(enemyArtOf(key), Number(im.dataset.px ?? TILE_PX), im.dataset.sil === '1');
        if (url) im.src = url;
      }
      if (imgs.length) setTimeout(step, 0);
    };
    // the focused entry's big portrait first
    imgs.sort((p, q) => (q.dataset.pk ? 1 : 0) - (p.dataset.pk ? 1 : 0));
    step();
  }

  private stats(): Sim { return this.base ?? (this.base = new Sim({ seed: 1, maxEnemies: 0 })); }

  private lockedHtml(tab: CodexTab, key: string, d: LockedDetail): string {
    const pic = tab === 'enemies' ? `<img class="cx-por big" data-pk="${key}" data-px="${BIG_PX}" data-sil="1" alt="">` : tab === 'fusions' ? '<span class="cx-fu"><span class="cx-q sm">?</span><i>+</i><span class="cx-q sm">?</span></span>' : '<span class="cx-q">?</span>';
    return `<div class="cx-head"><div class="cx-art${tab === 'enemies' ? ' por' : ''}">${pic}</div><div class="cx-ttl"><h3>???</h3><div class="cx-tags"><span>UNDISCOVERED</span></div></div></div>`
      + `<p class="cx-desc cx-lock">${d.hint}</p>`;
  }

  private detailHtml(d: CodexDetail): string {
    switch (d.tab) {
      case 'enemies': return this.enemyHtml(d);
      case 'skills': return this.skillHtml(d);
      case 'traits': return this.traitHtml(d);
      case 'boons': return this.boonHtml(d);
      case 'items': return this.itemHtml(d);
      case 'fusions': return this.fusionHtml(d);
      case 'forms': return this.formHtml(d);
    }
  }

  /** SURGE FORMS (owner 2026-11-03): the form's colours, blurb, numbers and what opens it */
  private formHtml(d: FormDetail): string {
    const prev = d.tier > 1 ? (detail('forms', catalogue('forms')[d.tier - 2], this.codex, true) as FormDetail) : null;
    return `<div class="cx-head"><div class="cx-art">${formSwatch(d.key, '')}</div><div class="cx-ttl"><h3>${d.name}</h3><div class="cx-tags"><span>SURGE FORM ${d.short}</span><span>${d.motif.toUpperCase()}</span></div></div></div>`
      + `<p class="cx-desc">${d.blurb}</p>`
      + `<div class="cx-sw">${(['outer', 'inner', 'core', 'accent'] as const).map((k) => `<div class="cx-swr"><i style="--c:${d.colors[k]}"></i><b>${{ outer: 'Outer flame', inner: 'Inner flame', core: 'Core', accent: 'Accent' }[k]}</b><span>${d.colors[k]}</span></div>`).join('')}</div>`
      + `<div class="cx-sec">WHILE TRANSFORMED <small>(Surge rank 1; rank 3 and Ascendant Form stretch every form)</small></div>`
      + `<div class="stats">${d.stats.map((s, i) => `<div class="st"><i>${s.label}</i><span class="v">${prev && prev.stats[i].value !== s.value ? `<s>${prev.stats[i].value}</s><span class="ar">→</span>` : ''}<b>${s.value}</b></span></div>`).join('')}</div>`
      + `<div class="cx-sec">HOW TO REACH IT</div><ul class="cx-notes"><li>${d.unlock}.</li><li>Charge the Surge bar to ${d.ki.toLocaleString('en-US')} Ki (this form's own stage holds ${d.stage.toLocaleString('en-US')}); transforming spends the whole bar.</li></ul>`
      + `<div class="cx-rec">Reached <b>${d.rec.reached}</b>×</div>`;
  }

  /** SKILL FUSIONS entry: the recipe (revealed when both partners stood at max rank), then — once fused — art, story, numbers */
  private fusionHtml(d: FusionDetail): string {
    const f = FUSIONS[d.key];
    const recipe = `<div class="cx-sec">RECIPE</div><div class="cx-fu-recipe"><span class="cx-pt">${techIcon(d.a)} ${d.aName}</span><i>rank ${MAX_WEAPON_LEVEL}</i><b>+</b><span class="cx-pt">${techIcon(d.b)} ${d.bName}</span><i>rank ${MAX_WEAPON_LEVEL}</i><b>➜</b><span class="cx-pt">${d.made ? fusionIcon(d.key) : '<span class="cx-q sm">?</span>'} ${d.name}</span></div>`;
    const tags = `<span class="t-evo">✦ FUSION</span>${d.made ? '' : '<span>RECIPE KNOWN</span>'}`;
    let html = `<div class="cx-head"><div class="cx-art${d.made ? '' : ' cx-dim'}">${d.made ? fusionIcon(d.key) : `<span class="cx-fu">${techIcon(d.a)}<i>+</i>${techIcon(d.b)}</span>`}</div><div class="cx-ttl"><h3>${d.name}</h3><div class="cx-tags">${tags}</div></div></div>`;
    if (!d.made) return html + recipe + '<p class="cx-lock">Fuse these two skills once (both at max rank, evolved or not) to learn what this fusion does.</p>' + `<div class="cx-rec">Recipe seen in <b>${d.rec.seen}</b> run${d.rec.seen === 1 ? '' : 's'}</div>`;
    html += `<p class="cx-desc"><i>${d.fantasy}</i></p><p class="cx-desc">${d.desc}</p>` + recipe;
    html += `<div class="cx-sec">NUMBERS <small>(base build)</small></div>${fusionStatGrid(this.stats(), d.key, 0)}`;
    html += `<p class="cx-note">Both skills leave your build and become this one, freeing a slot. A fusion always hits at least ${Math.round((EVO_FLOOR - 1) * 100)}% harder than the stronger of its partners' evolutions, evolved or not (the evolution floor); evolved partners can push it further (Resonance). Past 30:00 its signature hits also strip part of the health foes have grown (OVERTIME).</p>`;
    html += `<div class="cx-rec">Fused <b>${d.rec.made}</b>× · recipe seen in <b>${d.rec.seen}</b> run${d.rec.seen === 1 ? '' : 's'}</div>`;
    void f;
    return html;
  }

  private enemyHtml(d: EnemyDetail): string {
    const tag = d.variant === 'boss' ? '<span class="t-boss">BOSS</span>' : d.variant === 'elite' ? '<span class="t-elite">ELITE</span>' : '';
    return `<div class="cx-head"><div class="cx-art por"><img class="cx-por big" data-pk="${d.key}" data-px="${BIG_PX}" alt=""></div>`
      + `<div class="cx-ttl"><h3>${d.name}</h3><div class="cx-tags">${tag}<span>${d.role.toUpperCase()}</span></div></div></div>`
      + `<p class="cx-desc">${d.desc}</p>`
      + `<div class="stats">${d.stats.map((s) => `<div class="st"><i>${s.label}</i><span class="v"><b>${s.value}</b></span></div>`).join('')}</div>`
      + `<div class="cx-sec">BEHAVIOUR</div><ul class="cx-notes">${d.notes.map((n) => `<li>${n}</li>`).join('')}</ul>`
      + (d.forms ? `<div class="cx-sec">FORMS</div><ul class="cx-notes">${d.forms.map((f, i) => f.seen ? `<li><b>${i + 1}. ${f.name}</b> — ${f.desc}</li>` : `<li><b>${i + 1}. ???</b> — Not seen yet. Push it past ${i === 1 ? 'two thirds' : 'one third'} of its health.</li>`).join('')}</ul>` : '')
      + `<div class="cx-rec">Defeated <b>${d.defeated.toLocaleString('en-US')}</b> · met in <b>${d.met}</b> run${d.met === 1 ? '' : 's'}</div>`;
  }

  private skillHtml(d: SkillDetail): string {
    const sim = this.stats(), evo = d.evolution;
    const tags = [d.type ? `<span>${TYPE_LABEL[d.type]}</span>` : '', d.innate ? '<span>INNATE</span>' : '', evo && (this.all || d.rec.evolved > 0) ? '<span class="t-evo">★ EVOLVED</span>' : ''].join('');
    const r1 = skillStats(sim, d.key, 1, false), r5 = skillStats(sim, d.key, MAX_WEAPON_LEVEL, false);
    let html = `<div class="cx-head"><div class="cx-art">${techIcon(d.key)}</div><div class="cx-ttl"><h3>${d.name}</h3><div class="cx-tags">${tags}</div></div></div>`
      + `<div class="cx-sec">RANKS</div><ol class="cx-ranks">${d.ranks.map((r, i) => `<li><b>${i + 1}</b><span>${r}</span></li>`).join('')}</ol>`;
    if (r5.rows.length) html += `<div class="cx-sec">RANK 1 → RANK ${MAX_WEAPON_LEVEL} <small>(base build)</small></div>${statGrid(r5, r1)}`;
    html += '<div class="cx-sec">EVOLUTION</div>';
    if (evo) {
      const ev = skillStats(sim, d.key, MAX_WEAPON_LEVEL, true);
      html += `<div class="cx-evo"><div class="cx-evo-h"><span class="cx-art sm">${techIcon(d.key, true)}</span><b>${evo.name}</b></div>`
        + `<p>Rank ${MAX_WEAPON_LEVEL} + <span class="cx-pt">${traitIcon(evo.partner)} ${evo.partnerName}</span></p><p>${evo.desc}</p>${ev.rows.length ? statGrid(ev, r5) : ''}</div>`;
    } else html += '<p class="cx-lock">??? · Evolve this skill once to reveal its evolved form and the trait it needs.</p>';
    const r = d.rec;
    html += `<div class="cx-rec">Picked <b>${r.picked}</b>× · in <b>${r.runs}</b> run${r.runs === 1 ? '' : 's'} · best rank <b>${r.best || '–'}</b> · evolved <b>${r.evolved ? r.evolved + '×' : 'no'}</b></div>`;
    return html;
  }

  private traitHtml(d: TraitDetail): string {
    const sim = this.stats(), s1 = passiveStats(sim, d.key, 1), sm = passiveStats(sim, d.key, d.maxRank);
    let html = `<div class="cx-head"><div class="cx-art">${traitIcon(d.key)}</div><div class="cx-ttl"><h3>${d.name}</h3><div class="cx-tags"><span>TRAIT</span><span>MAX RANK ${d.maxRank}</span></div></div></div>`
      + `<p class="cx-desc">${d.desc}${d.maxRank > 1 && !/\brank\b/i.test(d.desc) ? ' (per rank)' : ''}</p>`;
    if (sm.rows.length) html += `<div class="cx-sec">${d.maxRank > 1 ? `RANK 1 → RANK ${d.maxRank}` : 'RANK 1'}</div>${statGrid(sm, d.maxRank > 1 ? s1 : null)}`;
    html += '<div class="cx-sec">EVOLVES</div>';
    html += d.evolves.length
      ? `<ul class="cx-evl">${d.evolves.map((e) => `<li>${e.known ? techIcon(e.id) : '<span class="cx-q sm">?</span>'} <b>${e.name}</b>${e.evolvedName ? ` → <span class="cx-evn">★ ${e.evolvedName}</span>` : e.known ? ' → <span class="cx-lock">???</span>' : ''}</li>`).join('')}</ul>`
      : '<p class="cx-lock">No skill evolves with this trait.</p>';
    const r = d.rec;
    html += `<div class="cx-rec">Picked <b>${r.picked}</b>× · in <b>${r.runs}</b> run${r.runs === 1 ? '' : 's'} · best rank <b>${r.best || '–'}</b></div>`;
    return html;
  }

  /** MAP PICKUPS (owner 2026-11-02) */
  private itemHtml(d: ItemDetail): string {
    return `<div class="cx-head"><div class="cx-art"><span class="cx-glyph">${d.icon}</span></div><div class="cx-ttl"><h3>${d.name}</h3><div class="cx-tags"><span>${d.kind}</span></div></div></div>`
      + `<p class="cx-desc">${d.desc}</p>${d.swatches.length ? `<div class="cx-sw">${d.swatches.map((w) => `<div class="cx-swr"><i style="--c:${w.css}"></i><b>${w.name}</b><span>${w.range} XP · ${w.colorName}</span><em>${w.shape}</em></div>`).join('')}</div>` : ''}`
      + d.notes.map((n) => `<p class="cx-note">${n}</p>`).join('')
      + `<div class="cx-rec">Found <b>${d.rec.found}</b>×</div>`;
  }

  private boonHtml(d: BoonDetail): string {
    return `<div class="cx-head"><div class="cx-art"><span class="cx-glyph">${d.icon}</span></div><div class="cx-ttl"><h3>${d.name}</h3><div class="cx-tags"><span>CONVERGENCE BOON</span></div></div></div>`
      + `<p class="cx-desc">${d.desc}</p><p class="cx-note">Offered when six Array Seals resonate (elites drop the seals).</p>`
      + `<div class="cx-rec">Chosen <b>${d.rec.picked}</b>×</div>`;
  }
}
